/* Prix B2B sur tout le catalogue — déclare notre thème auprès de l'app BSS.
 *
 * ROLLBACK : retirer la ligne `wb-bss-cartes.js` de `layout/theme.liquid`.
 * Une seule ligne, et tout le dispositif disparaît — hook, garde-fou CSS et
 * déclencheur sont dans ce seul fichier, exprès. Procédure exacte et commande
 * dans docs/bss-b2b/README.md, section « Rollback ».
 *
 * POURQUOI CE FICHIER EXISTE
 * --------------------------
 * L'app sait poser elle-même ses attributs de prix sur les cartes de listing
 * (`addCollectionPriceAttributes`), mais elle demande d'abord les sélecteurs de
 * la carte et du prix à une table codée en dur par NOM DE THÈME. Cette table ne
 * connaît que « Dawn » et « Maximize », plus douze thèmes de la famille Dawn.
 *
 * Notre thème s'annonce `Shopify.theme.schema_name = "Hyper"`. Il n'y est pas.
 * `getThemeSelectorForPage` rend donc `null` et la fonction sort à sa première
 * ligne : l'app n'a JAMAIS posé d'attribut sur nos cartes, et ne le fera jamais.
 * C'est la vraie raison pour laquelle le prix B2B n'apparaissait pas sur les PLP.
 *
 * Juste avant de rendre sa valeur, elle la passe par un filtre. C'est le point
 * d'entrée officiel, et c'est tout ce que ce fichier utilise :
 *
 *     u = BSS_B2B.custom?.executeFilter("custom:config_theme/installation", u, { page }, false)
 *
 * CE QUE ÇA CHANGE, ET CE QUE ÇA NE TOUCHE PAS
 * --------------------------------------------
 * En désignant comme prix le SEUL montant, l'app n'écrase que lui : le « TTC »
 * et le prix barré qui l'entourent survivent. Et comme c'est elle qui pose
 * l'attribut, elle traite l'élément par construction.
 *
 * ⛔ Aucun attribut de PRIX BSS n'est posé par Liquid. C'est la leçon du 09/09 :
 *    poser `bss-b2b-product-price` sur un élément le rend invisible par défaut (l'app
 *    le masque et ne le révèle qu'en y écrivant). Sur les cartes Typesense,
 *    peintes par notre front, elle ne venait jamais révéler — prix absent pour
 *    TOUS les visiteurs. Ici, rien n'est posé côté thème : si ce fichier
 *    n'obtient rien, la page reste exactement ce qu'elle est aujourd'hui.
 *
 * Périmètre :
 *   • cartes Typesense (PLP et recherche) ;
 *   • cartes Liquid (home, univers, populaires, nouveautés) ;
 *   • fiche produit.
 * Le panier et la quick view restent inchangés : un prix qui sera payé ne doit
 * pas être raccordé sans recette dédiée de son cycle complet.
 */
(function () {
  "use strict";

  var NOM_FILTRE = "custom:config_theme/installation";
  var FILTRE_DEBOUNCE = "custom:ProductSubject/StopDebounce";
  var EVENEMENT_REINIT_FICHE = "bss_b2b:CustomInitProductSubject";

  /* Le montant seul, jamais son conteneur.
   *
   * Le marqueur `data-wb-bss-price` vit DANS `.f-price-item`, autour du nombre
   * seulement. Le « TTC », l'éco-participation et le prix de référence sont ses
   * frères : l'app peut remplacer son `innerHTML` sans les emporter.
   *
   * Les deux branches sont exclusives — le thème masque `.f-price__sale` sans
   * `.f-price--on-sale`, et `.f-price__regular` avec — donc un seul élément
   * matche par carte. Les cartes Typesense ne portent jamais `--on-sale`
   * (`PRICE_MODIFIERS` ne vaut que `f-price--sold-out` ou rien) : c'est donc
   * toujours la seconde branche qui les sert, et c'est bien le bloc visible.
   */
  var MONTANT =
    ".f-price--on-sale .f-price-item--sale > [data-wb-bss-price]," +
    ".f-price:not(.f-price--on-sale) .f-price__regular " +
    ".f-price-item--regular > [data-wb-bss-price]";

  /* Même racine pour les cartes Liquid et les cartes peintes par Typesense.
   * Les anciens attributs de prix, posés autour du bloc des cartes Liquid par
   * l'installation, sont retirés solidairement du thème : il ne reste donc
   * jamais deux cibles BSS dans une carte.
   */
  var CARTES = ".product-card";

  /* Le conteneur de la fiche produit. Il n'existe que sur une PDP : partout
   * ailleurs (PLP, recherche, home), attendre son formulaire BSS ne peut rien
   * trouver. Webperf 25/09 : cette attente tournait 60 fois toutes les 500 ms
   * sur chaque PLP, puis une seconde fois à 12 s.
   */
  var FICHE = ".product__info-container";

  function estFiche() {
    return !!document.querySelector(FICHE);
  }

  /* B2B Solution — l'app qui définit `window.BSS_B2B` — ne se charge que pour
   * un client B2B (réglage du 24/09). Sans elle, réveiller BSS ne sert à rien et
   * coûte : un faux scroll exécute chaque écouteur de scroll de la page, à
   * chaque repeinte de la grille. ⛔ Le module de B2B Quotes LIT `BSS_B2B` mais
   * ne le définit pas : sa présence ne compte pas.
   */
  function bssCharge() {
    return typeof window.BSS_B2B !== "undefined" && window.BSS_B2B !== null;
  }

  /* La FICHE. `addProductPriceAttributes()` cherche `selectorForm` dans le
   * document, puis `selectorPrice` dedans, et pose ses attributs sur ce seul
   * élément — donc « TTC », « Éco-part inclus » et « Prix de référence », qui
   * l'entourent, SURVIVENT. C'est le seul moyen de les garder : aucune règle CSS
   * ne peut recréer des éléments détruits.
   *
   * ⛔ Solidaire du retrait de `bss-b2b-product-price` / `bss-b2b-variant-price`
   *    du `#price-…` de `snippets/product-information-blocks.liquid` : tant que
   *    le conteneur porte une cible, l'app y écrit et emporte tout le bloc.
   * Le formulaire BSS doit englober le prix ET le vrai formulaire d'achat :
   * sinon son detecteur ne trouve pas l'input `name="id"` de la variante et
   * `currentVariant` reste vide. `.product__info-container` contient les deux.
   */
  var SELECTEURS = {
    collection: { selectorCard: CARTES, selectorPrice: MONTANT },
    searchBar: { selectorCard: CARTES, selectorPrice: MONTANT },
    product: { selectorForm: ".product__info-container", selectorPrice: MONTANT },
  };

  /* Garde-fou : le prix ne doit jamais pouvoir rester invisible.
   *
   * L'app masque tout porteur de `bss-b2b-product-price` et ne le révèle qu'en y
   * écrivant. Or son flux ne notifie pas toujours (`productStorage.load()` peut
   * ne rien rendre) : les attributs resteraient posés sans que rien ne révèle.
   * C'est exactement ce qui a fait disparaître le prix en production le 09/09.
   *
   * La première règle porte sur les cartes ; la seconde sur la fiche. Sa
   * révélation à elle est un style INLINE, qui gagne toujours sur ces règles —
   * l'app garde donc le dernier mot, et on ne fait que garantir le prix public
   * en attendant qu'elle écrive.
   */
  var GARDE_FOU =
    "[bss-b2b-collection-element][bss-b2b-product-price]{visibility:visible}" +
    // Meme filet sur la FICHE : l'app y pose desormais l'attribut elle-meme, ce
    // qui declenche son `visibility: hidden`. Son flux se termine par un
    // `finally` qui revele, mais on ne parie pas la visibilite du prix sur un
    // chemin d'execution — c'est exactement ce qui l'a fait disparaitre le 09/09.
    ".product__info-container[bss-b2b-main-product-form] " +
    "[bss-b2b-product-price]{visibility:visible}";

  function poserGardeFou() {
    if (document.getElementById("wb-bss-cartes-style")) return;
    var style = document.createElement("style");
    style.id = "wb-bss-cartes-style";
    style.textContent = GARDE_FOU;
    (document.head || document.documentElement).appendChild(style);
  }

  /* `executeFilter` n'a AUCUN try/catch : une exception ici remonterait dans le
   * flux de prix de l'app. D'où l'enveloppe, et le repli sur la valeur reçue.
   */
  function brancherHook() {
    var hooks = (window.bssB2BHooks = window.bssB2BHooks || {});
    hooks.filters = hooks.filters || {};
    var precedent = hooks.filters[NOM_FILTRE];
    if (precedent && precedent.wbBssHyper) return;
    var filtre = function (valeur, contexte) {
      try {
        if (typeof precedent === "function") {
          valeur = precedent(valeur, contexte);
        }
        var page = contexte && contexte.page;
        return SELECTEURS[page] || valeur;
      } catch (erreur) {
        return valeur;
      }
    };
    filtre.wbBssHyper = true;
    hooks.filters[NOM_FILTRE] = filtre;
  }

  /* BSS attend normalement 1 seconde après le scroll avant de détecter les
   * nouvelles cartes. Ce filtre est exécuté juste avant la création de CE
   * debounce. On remplace donc uniquement son prochain appel, puis on restaure
   * immédiatement la primitive originale : panier, quantités et quick view ne
   * sont pas accélérés par effet de bord.
   */
  function brancherAcceleration() {
    var hooks = (window.bssB2BHooks = window.bssB2BHooks || {});
    hooks.filters = hooks.filters || {};
    var precedent = hooks.filters[FILTRE_DEBOUNCE];
    if (precedent && precedent.wbBssAcceleration) return;
    var filtre = function (valeur, contexte) {
      if (typeof precedent === "function") valeur = precedent(valeur, contexte);
      try {
        var bss = window.BSS_B2B;
        var original = bss && bss.utils && bss.utils.debounce;
        if (typeof original === "function" && !original.wbBssProchain) {
          var prochain = function (callback, attente) {
            bss.utils.debounce = original;
            return original(callback, attente === 1000 ? 50 : attente);
          };
          prochain.wbBssProchain = true;
          bss.utils.debounce = prochain;
        }
      } catch (erreur) {
        // Le comportement natif (valeur reçue) reste intact.
      }
      return valeur;
    };
    filtre.wbBssAcceleration = true;
    hooks.filters[FILTRE_DEBOUNCE] = filtre;
  }

  /* Les cartes Liquid de la home sont déjà dans le HTML quand BSS démarre.
   * Si notre hook arrive après son premier passage (cas observé), aucune carte
   * n'est ajoutée ensuite : l'observer DOM ne peut donc jamais les réveiller.
   *
   * BSS enregistre son écouteur public de réinitialisation AVANT son écouteur
   * `scroll`. On intercepte cette inscription, sans modifier BSS, puis on
   * envoie UN scroll différé : son propre flux `handleLazyProducts()` reprend
   * alors toutes les `.product-card` rendues par Liquid. Le délai est important :
   * à l'inscription, BSS n'a pas encore installé l'écouteur scroll qui vient
   * juste après dans son `init()`.
   */
  function reveillerPremierRendu() {
    var ajouter = window.addEventListener;
    var fini = false;
    window.addEventListener = function (type, ecouteur, options) {
      ajouter.call(window, type, ecouteur, options);
      if (fini || type !== EVENEMENT_REINIT_FICHE) return;
      fini = true;
      window.addEventListener = ajouter;
      setTimeout(function () {
        window.dispatchEvent(new Event("scroll"));
        rejouerPrixCartes();
      }, 100);
    };
    // L'app est optionnelle (notamment hors PROD) : ne jamais laisser un
    // remplacement de primitive globale si elle ne charge pas.
    setTimeout(function () {
      if (!fini && window.addEventListener !== ajouter) {
        window.addEventListener = ajouter;
      }
    }, 8000);
  }

  /* La phase BSS qui enregistre le formulaire produit précède parfois sa phase
   * de rendu du prix. Les cartes n'ont pas ce second état ; c'est pourquoi elles
   * pouvaient être justes alors que la fiche restait au prix public.
   *
   * On n'interprète AUCUNE règle ici : `showPrice(formId, variantId)` est la
   * fonction BSS elle-même, celle que son propre PriceFlow appelle. On attend
   * simplement que ses trois prérequis existent, puis on lui redonne la main.
   */
  function rejouerPrixFiche() {
    if (!estFiche()) return;
    var essais = 60;
    var tarifInitialise = false;
    function essai() {
      var fiche = document.querySelector(
        ".product__info-container[bss-b2b-product-form-id]"
      );
      var bss = window.BSS_B2B;
      var id = fiche && fiche.getAttribute("bss-b2b-product-form-id");
      var produit = id && bss && bss.storage && bss.storage.productFormStorage &&
        bss.storage.productFormStorage.get(id);
      var variante = produit && produit.currentVariant;
      var stockageVariantes = bss && bss.storage && bss.storage.variantStorage;
      if (variante && stockageVariantes) {
        // CP enrichit CET objet de storage, pas toujours la copie gardee sur
        // le formulaire produit. La clé de rendu vit donc ici en premier lieu.
        variante = stockageVariantes.get(variante.id) || variante;
      }
      var cle = variante && variante.bssB2bShowPriceKey;
      var moteurCp = bss && bss.cp && bss.cp.utils && bss.cp.utils.product;

      /* Au premier chargement, CP peut s'initialiser avant que ProductSubject
       * ait détecté la variante du formulaire. Dans ce cas il n'y a encore
       * aucune `bssB2bShowPriceKey` : refaire uniquement `showPrice` ne peut
       * rien afficher. On rejoue UNE fois le calcul natif CP, maintenant que
       * `currentVariant` existe, puis on relit la variante enrichie du storage.
       */
      if (variante && !cle && !tarifInitialise && moteurCp &&
          typeof moteurCp.initPriceList === "function") {
        tarifInitialise = true;
        try {
          moteurCp.initPriceList();
          if (stockageVariantes) {
            variante = stockageVariantes.get(variante.id) || variante;
            cle = variante.bssB2bShowPriceKey;
          }
        } catch (erreur) {
          // Le polling laisse encore a l'app le temps de finir son demarrage.
        }
      }
      var rendu = cle && bss[cle] && bss[cle].utils &&
        bss[cle].utils.product && bss[cle].utils.product.showPrice;

      if (typeof rendu === "function" && variante && variante.id) {
        try {
          // `showPrice` appelle `this.showPriceBase` dans le module CP : ne
          // jamais détacher la méthode de son objet propriétaire.
          rendu.call(bss[cle].utils.product, id, variante.id);
          return;
        } catch (erreur) {
          // BSS garde son prix public et son propre finally de visibilité.
        }
      }
      if (essais-- > 0) setTimeout(essai, 500);
    }
    essai();
  }

  /* Les cartes peuvent être ajoutées au storage APRES l'initialisation CP.
   * BSS les balise alors correctement, mais son PriceFlow ne recalcule rien.
   * On attend que chaque produit balisé existe dans son storage, puis on
   * rejoue ses deux méthodes natives de calcul et d'affichage de liste.
   */
  function rejouerPrixCartes() {
    var essais = 50;
    function essai() {
      var bss = window.BSS_B2B;
      var storage = bss && bss.storage && bss.storage.productStorage;
      var moteur = bss && bss.cp && bss.cp.utils && bss.cp.utils.productList;
      var cibles = document.querySelectorAll(
        "[bss-b2b-collection-element][bss-b2b-product-price]" +
        "[bss-b2b-product-id]"
      );
      if (storage && moteur && cibles.length &&
          typeof moteur.initPriceList === "function" &&
          typeof moteur.showPrice === "function") {
        var ids = [];
        var manquants = 0;
        for (var i = 0; i < cibles.length; i++) {
          var id = Number(cibles[i].getAttribute("bss-b2b-product-id"));
          if (!id || ids.indexOf(id) !== -1) continue;
          if (storage.get(id)) ids.push(id);
          else manquants += 1;
        }
        if (ids.length) {
          try {
            moteur.initPriceList();
            for (var j = 0; j < ids.length; j++) {
              moteur.showPrice.call(moteur, ids[j]);
            }
          } catch (erreur) {
            // Un prochain essai ou le flux natif BSS garde la main.
          }
        }
        if (!manquants) return;
      }
      if (essais-- > 0) setTimeout(essai, 100);
    }
    essai();
  }

  /* L'app ne redétecte les cartes ajoutées qu'au SCROLL (`handleLazyProducts`,
   * debounce 1 s). La recherche, les PLP Typesense et les onglets de collections
   * peignent après le chargement : sans ce coup de pouce, leur prix ne change
   * qu'après un scroll du visiteur.
   *
   * On observe le document, car `.wb-grid` de la recherche n'existe qu'après sa
   * première réponse. Seuls les ajouts qui CONTIENNENT une `.product-card` nous
   * intéressent. Les écritures de BSS (attributs, span de prix, table de quantité)
   * ne satisfont jamais ce test : pas de boucle malgré `subtree: true`.
   *
   * Sur la fiche, un changement de variante remplace le bloc de prix par le HTML
   * d'une section Shopify. Le nouveau marqueur déclenche l'événement public de
   * réinitialisation de BSS, afin que la cible étroite récupère les attributs.
   */
  var PLAFOND = 60;
  var DELAI = 50;

  function brancherDeclencheur() {
    if (typeof MutationObserver !== "function") return;
    var racine = document.body || document.documentElement;
    if (!racine) return;

    var restant = PLAFOND;
    var minuteurCartes = null;
    var minuteurFiche = null;
    // Lu une fois : au démarrage, le document est entièrement analysé.
    var surFiche = estFiche();

    function reveillerCartes() {
      minuteurCartes = null;
      if (restant-- <= 0) return;
      window.dispatchEvent(new Event("scroll"));
      rejouerPrixCartes();
    }

    function reveillerFiche() {
      minuteurFiche = null;
      if (restant-- <= 0) return;
      window.dispatchEvent(new Event(EVENEMENT_REINIT_FICHE));
      setTimeout(rejouerPrixFiche, DELAI + 200);
    }

    function contient(noeud, selecteur) {
      return noeud && noeud.nodeType === 1 &&
        ((noeud.matches && noeud.matches(selecteur)) ||
         (noeud.querySelector && noeud.querySelector(selecteur)));
    }

    var observer = new MutationObserver(function (mutations) {
      if (restant <= 0) return void observer.disconnect();
      // BSS se charge en `defer` APRÈS ce fichier : on le cherche à chaque lot
      // de mutations, jamais une fois pour toutes au démarrage.
      var avecBss = bssCharge();
      if (!avecBss && !surFiche) return;
      var cartes = false;
      var fiche = false;
      for (var i = 0; i < mutations.length; i++) {
        var ajoutes = mutations[i].addedNodes || [];
        for (var j = 0; j < ajoutes.length; j++) {
          cartes = cartes || (avecBss && contient(ajoutes[j], CARTES));
          fiche = fiche || (surFiche && contient(
            ajoutes[j],
            ".product__info-container [data-wb-bss-price]" +
              ":not([bss-b2b-product-price])"
          ));
        }
      }
      if (cartes) {
        if (minuteurCartes) clearTimeout(minuteurCartes);
        minuteurCartes = setTimeout(reveillerCartes, DELAI);
      }
      if (fiche) {
        if (minuteurFiche) clearTimeout(minuteurFiche);
        minuteurFiche = setTimeout(reveillerFiche, DELAI);
      }
    });

    observer.observe(racine, { childList: true, subtree: true });
  }

  function demarrer() {
    poserGardeFou();
    brancherDeclencheur();
    rejouerPrixFiche();
    // Filet pour les chargements BSS particulièrement lents observés en prod.
    if (estFiche()) setTimeout(rejouerPrixFiche, 12000);
  }

  // Le hook doit exister avant le premier appel de l'app ; le reste peut
  // attendre le DOM. `executeFilter` relit le global à chaque appel, donc un
  // branchement tardif serait rattrapé — on ne s'en remet pas à cela pour
  // autant.
  brancherHook();
  brancherAcceleration();
  reveillerPremierRendu();
  ["bss_b2b:module:loaded", "bss_b2b:product-form:loaded"].forEach(
    function (evenement) {
      window.addEventListener(evenement, function () {
        setTimeout(rejouerPrixFiche, 50);
      });
    }
  );

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", demarrer);
  } else {
    demarrer();
  }
})();
